import { Component, ErrorHandler, Inject, Injector, OnDestroy, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core'
import { FieldTree, form, required, validate } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { AsiakkaanKirjanpidonPeruste, KirjanpidonPeruste } from '@lemon/core-firebase/model/kayttaja'

import { DateService } from '@lemon/core/service/date.service'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'
import { KirjauksenKulunJaksotusRequest, KirjauksenTallennusvastaus, Kirjaus, Kirjausrivi } from 'app/_jaettu-lemonator/model/kirjanpito'
import { AsiakasJaettuService } from 'app/_jaettu-lemonator/service/asiakas-jaettu.service'
import { combineLatest, map, Subject, take, takeUntil } from 'rxjs'

export interface KirjausJaksotaKuluDialogData {
  kirjaus: Kirjaus
  asiakas: Asiakas
  kirjauksenKulurivit: Kirjausrivi[]
}

interface KulunJaksotusLomake {
  ekaKuukausi: Date | null
  vikaKuukausi: Date | null
}

@Component({
  templateUrl: './kirjaus-jaksota-kulu.dialog.html',
  styleUrl: './kirjaus-jaksota-kulu.dialog.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjausJaksotaKuluDialog implements OnInit, OnDestroy {
  lomake: FieldTree<KulunJaksotusLomake>

  readonly kuukausienLukumaara = signal<number>(undefined)
  readonly kulut = signal<{ tili: string; yhteensa: number; perKuukausi: number }[]>(undefined)

  virhe: string = ''

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)
  private readonly _ekaKuukausiLoppuEnnenAlkua = signal(false)
  private readonly _vikaKuukausiLoppuEnnenAlkua = signal(false)

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: KirjausJaksotaKuluDialogData,
    private _dialogRef: MatDialogRef<KirjausJaksotaKuluDialog>,
    private _errorHandler: ErrorHandler,
    private _dateService: DateService,
    private _formValidationService: FormValidationService,
    private _ladataanService: LadataanService,
    private _firebase: FirebaseLemonator,
    private _asiakasService: AsiakasService,
    private _asiakasJaettuService: AsiakasJaettuService,
  ) {
    this.lomake = form(signal<KulunJaksotusLomake>({ ekaKuukausi: null, vikaKuukausi: null }), polku => {
      required(polku.ekaKuukausi)
      validate(polku.ekaKuukausi, () => (this._ekaKuukausiLoppuEnnenAlkua() ? { kind: 'loppuEnnenAlkua' } : null))
      required(polku.vikaKuukausi)
      validate(polku.vikaKuukausi, () => (this._vikaKuukausiLoppuEnnenAlkua() ? { kind: 'loppuEnnenAlkua' } : null))
    })
    // Kuten reaktiivisessa lomakkeessa, järjestys tarkistetaan vain sille kentälle, jonka arvo muuttui
    arvonMuutokset(this.lomake.ekaKuukausi)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => this._ekaKuukausiLoppuEnnenAlkua.set(this._onkoLoppuEnnenAlkua()))
    arvonMuutokset(this.lomake.vikaKuukausi)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => this._vikaKuukausiLoppuEnnenAlkua.set(this._onkoLoppuEnnenAlkua()))
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  ngOnInit() {
    combineLatest([arvonMuutokset(this.lomake.ekaKuukausi, { injector: this._injector }), arvonMuutokset(this.lomake.vikaKuukausi, { injector: this._injector })])
      .pipe(
        takeUntil(this._ngUnsubscribe),
        map(([eka, vika]) => {
          if (!eka || !vika) {
            return null
          }
          return this._dateService.kuukausiaValissa(vika, eka)
        }),
      )
      .subscribe(kuukausiaValissa => {
        if (kuukausiaValissa !== null && kuukausiaValissa !== undefined) {
          const kuukausienLukumaara = kuukausiaValissa + 1
          this.kuukausienLukumaara.set(kuukausienLukumaara)

          this.kulut.set(
            this.data.kirjauksenKulurivit.map(rivi => {
              const yhteensa = rivi.d || 0 || -(rivi.k || 0)
              return {
                tili: rivi.t,
                yhteensa: yhteensa,
                perKuukausi: !!kuukausienLukumaara ? yhteensa / kuukausienLukumaara : null,
              }
            }),
          )
        }
      })

    this._asiakasService.nykyisenAsiakkaanTilikaudetObservable
      .pipe(
        take(1),
        map(tilikaudet => this._asiakasJaettuService.annaTilikausiPaivalle({ tilikaudet: tilikaudet }, this._dateService.currentLocalDate())),
      )
      .subscribe(tilikausi => {
        if (tilikausi) {
          asetaArvo(this.lomake.vikaKuukausi, this._dateService.kuukaudenViimeinen(this._dateService.localDateToDate(tilikausi.loppuu)))
        }
      })

    const kirjauksenPaivamaara = this.data.kirjaus.p
    const asDate = this._dateService.kuukaudenEnsimmainen(this._dateService.numberToDate(kirjauksenPaivamaara))

    asetaArvo(this.lomake.ekaKuukausi, asDate)
  }

  jaaKulu() {
    this.virhe = null

    if (this.lomake().invalid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    const formattedMaksuperusteisuudenJaksot = this._annaValitunAikavalinMaksuperusteisetJaksot(
      this.lomake.ekaKuukausi().value(),
      this.lomake.vikaKuukausi().value(),
      this.data.asiakas.kirjanpidonPerusteet,
    ).map(jakso => this._dateService.muotoilePaikallinenPaiva(jakso.alkaa, 'fi') + (jakso.loppuu ? ' - ' + this._dateService.muotoilePaikallinenPaiva(jakso.loppuu, 'fi') : '...'))
    if (formattedMaksuperusteisuudenJaksot.length) {
      this.virhe = 'Valitulla aikavälillä on maksuperusteisia jaksoja: ' + formattedMaksuperusteisuudenJaksot.join(', ')
      return
    }

    const pyynto: KirjauksenKulunJaksotusRequest = {
      asiakasAvain: this.data.asiakas.avain,
      kirjaus: this.data.kirjaus,
      alku: this._dateService.numberDateToNumberMonth(this._dateService.dateToNumber(this.lomake.ekaKuukausi().value())),
      loppu: this._dateService.numberDateToNumberMonth(this._dateService.dateToNumber(this.lomake.vikaKuukausi().value())),
      kulurivit: this.data.kirjauksenKulurivit,
    }

    this._ladataanService.aloitaLataaminen()

    this._firebase
      .functionsCall<KirjauksenKulunJaksotusRequest, KirjauksenTallennusvastaus>('kirjanpitoKirjauksetJaksotaKuluKuukausille', pyynto, { timeout: 540 * 1000 })
      .then(() => {
        this._dialogRef.close()
      })
      .catch(err => this._errorHandler.handleError(err))
      .finally(() => this._ladataanService.lopetaLataaminen())
  }

  private _onkoLoppuEnnenAlkua() {
    const eka = this.lomake.ekaKuukausi().value()
    const vika = this.lomake.vikaKuukausi().value()
    return !!(vika && eka && eka > vika)
  }

  private _annaValitunAikavalinMaksuperusteisetJaksot(
    eka: Date,
    vika: Date,
    perusteet: AsiakkaanKirjanpidonPeruste[],
  ): { alkaa: AsiakkaanKirjanpidonPeruste['alkaa']; loppuu: AsiakkaanKirjanpidonPeruste['loppuu'] }[] {
    const output: { alkaa: AsiakkaanKirjanpidonPeruste['alkaa']; loppuu: AsiakkaanKirjanpidonPeruste['loppuu'] }[] = []

    for (const peruste of perusteet) {
      if (peruste.peruste !== KirjanpidonPeruste.MAKSU) {
        continue
      }

      if (this._dateService.localDateToDate(peruste.alkaa) > vika || (peruste.loppuu && this._dateService.localDateToDate(peruste.loppuu) < eka)) {
        continue
      }

      output.push({ alkaa: peruste.alkaa, loppuu: peruste.loppuu })
    }
    return output
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
